import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DynamicPropertyPage } from './DynamicPropertyPage';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { CreditCard, Check, Loader2, Mail, Pencil } from 'lucide-react';
import { API, isAdminAuthenticated } from '../lib/api';

export const PreviewPage = () => {
  const navigate = useNavigate();
  const { license_number } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const manageTokenFromUrl = searchParams.get('manage_token');
  
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const canUseAdminEditRoute = isAdminAuthenticated();
  const manageTokenFromSession = sessionStorage.getItem(`preview_manage_token:${license_number}`);
  const previewManageToken = manageTokenFromUrl || manageTokenFromSession;

  useEffect(() => {
    if (manageTokenFromUrl && license_number) {
      sessionStorage.setItem(`preview_manage_token:${license_number}`, manageTokenFromUrl);
    }
  }, [manageTokenFromUrl, license_number]);

  const fetchProperty = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties/${license_number}`);
      setProperty(response.data);
    } catch (error) {
      console.error('Error fetching property:', error);
      toast.error('Error al cargar la propiedad');
    } finally {
      setLoading(false);
    }
  }, [license_number]);

  const checkPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;

    if (attempts >= maxAttempts) {
      toast.error('Error verificando el pago. Por favor contacta con soporte.');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);

      if (response.data.payment_status === 'paid') {
        toast.success('¡Pago completado con éxito! Tu sitio será publicado en breve.');
        fetchProperty();
        setCheckingPayment(false);
      } else if (response.data.status === 'expired') {
        toast.error('La sesión de pago ha expirado. Por favor intenta de nuevo.');
        setCheckingPayment(false);
      } else {
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      toast.error('Error verificando el pago');
      setCheckingPayment(false);
    }
  }, [fetchProperty]);

  useEffect(() => {
    fetchProperty();
  }, [fetchProperty]);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [sessionId, checkPaymentStatus]);


  const handlePayment = async () => {
    setPaymentLoading(true);
    
    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/payments/checkout/session`, {
        package_id: property.plan,
        license_number: license_number,
        origin_url: originUrl
      });
      
      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Error al iniciar el pago');
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Propiedad no encontrada</p>
      </div>
    );
  }

  const isPaid = property.payment?.paid;
  const isPublished = property.publication?.published;
  const canEditFromPreview = (!isPaid || !isPublished) && Boolean(previewManageToken || canUseAdminEditRoute);
  const planPrices = { basic: 49, plus: 69, premium: 89 };
  const planPrice = planPrices[property.plan];

  return (
    <>
      {checkingPayment && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[70] bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Verificando pago...</span>
        </div>
      )}
      
      {!isPaid && (
        <div id="preview-banner" className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-4 fixed bottom-0 left-0 right-0 z-[51]">
          <div className="container mx-auto px-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="font-semibold">Vista Previa - Plan {property.plan.charAt(0).toUpperCase() + property.plan.slice(1)}</p>
              <p className="text-sm opacity-90">Completa el pago para publicar tu sitio web</p>
              <a
                href="mailto:info@miwebvacacional.online"
                className="mt-1 inline-flex items-center gap-2 text-sm text-white/90 hover:text-white"
              >
                <Mail className="h-4 w-4" />
                <span>¿Tienes dudas? Escríbenos a</span>
                info@miwebvacacional.online
              </a>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              {canEditFromPreview && (
                <Button
                  onClick={() => {
                    if (previewManageToken) {
                      navigate(`/manage/${previewManageToken}/edit`);
                      return;
                    }
                    navigate(`/submit/${license_number}/edit`);
                  }}
                  size="lg"
                  variant="secondary"
                  className="bg-white text-gray-900 hover:bg-gray-100"
                >
                  <Pencil className="h-5 w-5 mr-2" />
                  Volver a editar
                </Button>
              )}
              <Button
                onClick={handlePayment}
                disabled={paymentLoading}
                size="lg"
                variant="secondary"
                className="bg-white text-blue-600 hover:bg-gray-100"
              >
                {paymentLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5 mr-2" />
                    Pagar {planPrice}€
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {isPaid && (
        <div className="bg-green-600 text-white py-4">
          <div className="container mx-auto px-4 flex items-center justify-center gap-3">
            <Check className="h-6 w-6" />
            <p className="font-semibold">Pago completado - Tu sitio será publicado en breve</p>
          </div>
        </div>
      )}

      <DynamicPropertyPage showEditButton={canEditFromPreview} />
    </>
  );
};
